// Updated Mock data for IpFunders - Option-Based IP Funding Platform
// Homepage: Implicit optionality, invitation not term sheet
// How It Works: Explicit progression and natural evolutions

// ===== HOMEPAGE CONTENT (Implicit Optionality) =====

export const trustSignals = [
  {
    id: 1,
    title: "No Premature Commitment",
    description: "Start protecting ideas without locking in large upfront costs. Commitment grows only as value becomes clear.",
    icon: "Filter"
  },
  {
    id: 2,
    title: "Costs Scale With Success",
    description: "Early payments stay modest. Investment increases only after patents issue and the portfolio proves useful.",
    icon: "TrendingUp"
  },
  {
    id: 3,
    title: "Risk Stays With Us",
    description: "We fund prosecution and absorb uncertainty upfront. You retain flexibility throughout.",
    icon: "ArrowLeftRight"
  },
  {
    id: 4,
    title: "You Stay In Control",
    description: "No forced outcomes, no surprises. Simplify your position whenever it makes sense.",
    icon: "Shield"
  }
];

export const traditionalModel = {
  title: "Traditional IP Model",
  subtitle: "High commitment before clarity.",
  points: [
    { label: "Large Upfront Investment", description: "$30k-$150k+ before knowing if the patent will issue" },
    { label: "Commitment Before Validation", description: "You pay in full while the idea is still unproven" },
    { label: "All Risk Falls on You", description: "Grant uncertainty, office actions, market timing" },
    { label: "No Room to Adapt", description: "Once committed, changing course is expensive" }
  ]
};

export const ipfundersModel = {
  title: "IpFunders Model",
  subtitle: "Commitment aligns with clarity.",
  points: [
    { label: "Modest Early Investment", description: "Low periodic fees while applications are pending" },
    { label: "Costs Follow Progress", description: "Investment grows only as the IP proves valuable" },
    { label: "Prosecution Risk Absorbed", description: "We fund and manage the process upfront" },
    { label: "Flexibility Preserved", description: "Adapt your position as the business evolves" }
  ]
};

export const problemPoints = [
  {
    title: "Commitment Before Clarity",
    description: "Traditional prosecution demands full investment before you know if the idea will work"
  },
  {
    title: "No Downside Protection",
    description: "If the market shifts or the patent doesn't issue, you've already spent $30k-$150k+"
  },
  {
    title: "Timing Mismatch",
    description: "IP costs hit hardest when startups can least afford them"
  },
  {
    title: "Strategic Inflexibility",
    description: "Once you start, changing course is difficult and expensive"
  }
];

export const solutionPoints = [
  {
    title: "Start Without Over-Committing",
    description: "Protect ideas early while preserving capital for product and growth",
    icon: "Search"
  },
  {
    title: "Investment Follows Value",
    description: "Costs increase only as patents issue and the portfolio expands",
    icon: "Coins"
  },
  {
    title: "We Manage Prosecution",
    description: "U.S. patent prosecution handled internally, enabling efficient strategy",
    icon: "Shield"
  },
  {
    title: "Simplify When Ready",
    description: "Consolidate ownership and freeze future costs whenever it makes sense",
    icon: "Key"
  }
];

export const pricingPhilosophy = {
  headline: "Economics That Align With Progress",
  subheadline: "You're not paying for a patent. You're paying for time to figure out whether the IP matters.",
  principles: [
    {
      title: "Early Payments Are Intentionally Modest",
      description: "While applications are pending, costs stay low. This is the period of greatest uncertainty, so we keep your exposure minimal.",
      icon: "Clock"
    },
    {
      title: "Investment Grows With Proven Value",
      description: "Costs increase only after patents issue and the portfolio expands. You invest more only when the IP has demonstrated worth.",
      icon: "TrendingUp"
    },
    {
      title: "Consolidation Is Always Available",
      description: "At any point, you can simplify ownership and eliminate ongoing fees. Many founders prefer this once the IP becomes strategic.",
      icon: "Key"
    }
  ],
  footnote: "Actual terms vary based on technology area and portfolio scope. We discuss specifics after evaluation."
};

export const howItWorksSteps = [
  {
    step: 1,
    title: "Share Your Idea",
    description: "We evaluate technical merit and strategic potential under NDA.",
    icon: "FileSearch"
  },
  {
    step: 2,
    title: "We Handle Prosecution",
    description: "If selected, we fund and manage U.S. patent prosecution from the start.",
    icon: "CircleDollarSign"
  },
  {
    step: 3,
    title: "You Focus on Building",
    description: "Modest periodic fees while applications develop. Your capital stays in the business.",
    icon: "Cog"
  },
  {
    step: 4,
    title: "Adapt as You Learn",
    description: "As value becomes clear, adjust your IP position to match your strategy.",
    icon: "Shuffle"
  }
];

export const audienceTypes = [
  {
    id: "startups",
    title: "Startups & Founders",
    description: "Protect ideas without diverting capital from product and growth.",
    icon: "Rocket",
    link: "/startups"
  },
  {
    id: "corporate",
    title: "Corporate R&D Teams",
    description: "Pursue more ideas without budget commitment. Invest in what proves valuable.",
    icon: "Building2",
    link: "/corporate"
  },
  {
    id: "inventors",
    title: "Independent Inventors",
    description: "Access professional IP development without personal financial exposure.",
    icon: "Lightbulb",
    link: "/inventors"
  },
  {
    id: "investors",
    title: "Investors & Partners",
    description: "Explore capital partnership opportunities in IP asset development.",
    icon: "Landmark",
    link: "/investors"
  }
];

// ===== HOW IT WORKS PAGE CONTENT (Explicit Progression) =====

export const howItWorksPageContent = {
  hero: {
    headline: "How the Relationship Evolves",
    subheadline: "A natural progression from early protection to strategic clarity."
  },
  modelFraming: "IpFunders applies a staged funding model to IP protection. Today, this model is most commonly applied to patent prosecution.",
  intro: "IpFunders is designed for founders who want IP protection without premature commitment. Here's how things typically unfold over time.",
  
  stages: [
    {
      title: "Getting Started",
      subtitle: "We step in early",
      description: "You share your idea under NDA. We evaluate technical merit and strategic potential. If there's a fit, we fund and manage U.S. patent prosecution from the beginning.",
      keyPoints: [
        "Low initial commitment",
        "Focus on protecting the idea without over-investing",
        "You retain operational control",
        "We handle the complexity of prosecution"
      ],
      icon: "FileSearch"
    },
    {
      title: "As the IP Takes Shape",
      subtitle: "Investment follows progress",
      description: "While applications are pending, your payments stay modest. This is the period of greatest uncertainty. We keep your exposure low until the IP proves itself.",
      keyPoints: [
        "Periodic fees remain intentionally affordable",
        "Patents issue and scope becomes clearer",
        "Investment grows only as value is demonstrated",
        "No large commitments while outcomes are uncertain"
      ],
      icon: "Cog"
    },
    {
      title: "When the IP Becomes Strategic",
      subtitle: "Clarity enables action",
      description: "At some point, many founders prefer to simplify ownership and eliminate ongoing fees. When the IP is clearly valuable to your business, consolidating your position often makes sense.",
      keyPoints: [
        "Simplify ownership when the time is right",
        "Freeze future costs permanently",
        "Full control passes to you",
        "No obligation, no deadline"
      ],
      icon: "Key"
    },
    {
      title: "Ongoing Growth",
      subtitle: "Expand as needed",
      description: "As your business grows, your IP needs may expand. Continuations and divisionals can broaden protection. We manage prosecution efficiently, without renegotiation every time.",
      keyPoints: [
        "Expand the patent family as strategy requires",
        "Costs scale with scope, not with arbitrary triggers",
        "Internal prosecution enables fast execution",
        "No surprises as the portfolio grows"
      ],
      icon: "GitBranch"
    },
    {
      title: "Always In Your Control",
      subtitle: "No forced outcomes",
      description: "Throughout the relationship, you retain control. If the business pivots, if the market shifts, if the idea doesn't pan out, you can adapt. There's no forced transfer, no surprise obligation, no upfront over-commitment.",
      keyPoints: [
        "Your strategy drives decisions",
        "No penalties for changing direction",
        "Flexibility built into the structure",
        "We succeed when you succeed"
      ],
      icon: "Shield"
    }
  ],
  
  foreignFilings: {
    title: "A Note on Foreign Patents",
    description: "Foreign patent filings are optional and only pursued if you elect to do so. The core relationship focuses on U.S. prosecution. International expansion is always your choice."
  },
  
  whatWeAreNot: {
    title: "What IpFunders Is Not",
    points: [
      "Not a patent law firm providing legal advice",
      "Not a litigation funder seeking enforcement revenue",
      "Not a venture fund requiring equity",
      "Not a lender expecting repayment"
    ]
  }
};

// ===== FAQ CONTENT (Explicit Options Here) =====

export const founderFAQ = [
  {
    question: "Why wouldn't I just pay for the patent myself?",
    answer: "You could. But traditional prosecution requires $30k-$150k+ upfront with no guarantee of outcome. With IpFunders, your early investment stays modest while uncertainty is highest. If the idea doesn't work out, your exposure is limited. If it does, you can consolidate ownership when the value is clear. You're paying for flexibility and downside protection, not just prosecution."
  },
  {
    question: "What happens if the idea doesn't work out?",
    answer: "You adapt. If the business pivots, if the market shifts, or if the patent doesn't issue as expected, you're not locked into a large sunk cost. Your periodic fees bought you time and protection without forcing a commitment you couldn't unwind. There's no penalty for changing course."
  },
  {
    question: "What happens if the patent becomes very valuable?",
    answer: "You can simplify ownership whenever it makes sense. When IP becomes strategic to your business, many founders prefer to consolidate their position and eliminate ongoing fees. The terms for doing so are predictable and designed to be straightforward."
  },
  {
    question: "Do I give up ownership of my IP?",
    answer: "No. Your company remains the owner of its core technology and continues to control how it is used in your business. IpFunders funds the protection of the IP asset and aligns returns to the success of that protection, but does not take equity in your company or assume operational control. Your ability to build, operate, and commercialize your technology is not restricted by the funding structure."
  },
  {
    question: "What happens if the patent is not granted?",
    answer: "If a patent application does not result in a granted patent, your obligation is limited. IpFunders assumes a significant portion of the risk associated with pursuing protection. Because funding is staged and tied to progress, commitment increases only as the likelihood of success improves. If protection fails early, costs are limited and not treated as traditional debt owed by the company."
  },
  {
    question: "Is IpFunders equity or debt financing? How should I think about it?",
    answer: "IpFunders is not equity financing and not traditional debt. We fund the creation of IP assets without taking ownership in your company or requiring upfront repayment tied to your balance sheet. The model can be understood through familiar financial analogies: comparable to a patent lease-back where IpFunders funds the creation of the IP asset while the operating company retains usage rights through a licensing structure; comparable to an IP sale-leaseback where capital is deployed against an asset under development, with usage rights preserved for the company and returns tied to asset success; and non-dilutive to equity holders because funding is tied to IP assets rather than ownership in the operating company, so founders retain full equity control."
  },
  {
    question: "When do I start paying IpFunders?",
    answer: "Payment obligations are aligned with progress and outcomes, not upfront legal activity. Rather than paying the full cost of protection at the beginning, commitment is structured to increase as the IP asset advances toward a successful outcome. This allows companies to pursue protection without bearing the full financial burden before value is established."
  },
  {
    question: "How is IpFunders' return aligned with my company's success?",
    answer: "IpFunders' returns are tied to the success of the IP asset being protected, not to the operating performance or equity value of your company. Because funding is structured around the creation and outcome of the IP itself, IpFunders is incentivized to support protection strategies that result in durable, defensible assets. The model avoids misalignment common in service-based billing or equity financing, where cost is incurred regardless of outcome or incentives diverge from asset quality."
  },
  {
    question: "Why would I pay periodic fees for something I might not keep?",
    answer: "Because you're paying for time and flexibility, not a binding commitment. The fees keep your options open while someone else absorbs prosecution risk and cost. When you have more clarity about whether the IP matters, you decide how to proceed. That flexibility is valuable, especially when you can't predict which ideas will win."
  }
];

// ===== PRICING PAGE CONTENT (Explicit Stages Here) =====

export const pricingPageContent = {
  headline: "How Economics Respond to Success",
  subheadline: "Costs are structured to align with progress, not to extract value upfront.",
  
  stages: [
    {
      stage: "While Pending",
      description: "Low periodic fees while applications work through the USPTO",
      behavior: "Your exposure stays minimal during the period of greatest uncertainty.",
      range: "Low"
    },
    {
      stage: "After Issuance",
      description: "Fees increase modestly once a patent is granted",
      behavior: "Investment grows only after the asset has proven value.",
      range: "Moderate"
    },
    {
      stage: "Portfolio Expansion",
      description: "Additional fees if you choose to file continuations or divisionals",
      behavior: "Scale costs only when you scale protection.",
      range: "Varies"
    },
    {
      stage: "Consolidation",
      description: "Simplify ownership and eliminate ongoing fees",
      behavior: "Freeze all future costs. Full control passes to you.",
      range: "Predictable"
    }
  ],
  
  example: {
    title: "Illustrative Timeline (Non-Binding)",
    timeline: [
      { year: "Year 1-3", status: "Application Pending", cost: "~$2k-5k/year", note: "Low fees while you validate the market" },
      { year: "Year 4+", status: "Patent Issued", cost: "~$5k-15k/year", note: "Fees increase after grant" },
      { year: "When Ready", status: "Consolidation", cost: "Predictable", note: "Eliminates all future fees" }
    ],
    disclaimer: "Actual terms vary based on technology area, claim scope, and portfolio size. This is illustrative only."
  }
};

// ===== OTHER PAGE CONTENT =====

export const footerLinks = {
  company: [
    { label: "Why IpFunders", href: "/why-ipfunders" },
    { label: "How It Works", href: "/how-it-works" },
    { label: "Contact", href: "/contact" }
  ],
  forYou: [
    { label: "Startups", href: "/startups" },
    { label: "Corporate", href: "/corporate" },
    { label: "Inventors", href: "/inventors" },
    { label: "Investors", href: "/investors" }
  ],
  legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "Cookie Policy", href: "/cookies" },
    { label: "NDA Process", href: "/nda" }
  ]
};

export const navLinks = [
  { label: "How It Works", href: "/how-it-works" },
  { label: "Why IpFunders", href: "/why-ipfunders" },
  { label: "Startups", href: "/startups" },
  { label: "Corporate", href: "/corporate" },
  { label: "Investors", href: "/investors" },
  { label: "Contact", href: "/contact" }
];

export const roleOptions = [
  { value: "founder", label: "Startup Founder / CEO" },
  { value: "rd", label: "Corporate R&D / Innovation" },
  { value: "inventor", label: "Independent Inventor" },
  { value: "investor", label: "Investor / Strategic Partner" },
  { value: "other", label: "Other" }
];

export const industryOptions = [
  { value: "biotech", label: "Biotechnology & Life Sciences" },
  { value: "cleantech", label: "Clean Technology & Energy" },
  { value: "software", label: "Software & AI" },
  { value: "hardware", label: "Hardware & Electronics" },
  { value: "materials", label: "Advanced Materials" },
  { value: "medical", label: "Medical Devices" },
  { value: "aerospace", label: "Aerospace & Defense" },
  { value: "other", label: "Other" }
];

export const patentStatusOptions = [
  { value: "concept", label: "Concept / Pre-Development" },
  { value: "prototype", label: "Prototype / Early Development" },
  { value: "provisional", label: "Provisional Application Filed" },
  { value: "pending", label: "Patent Pending" },
  { value: "issued", label: "Patent Issued" },
  { value: "portfolio", label: "Multiple Patents / Portfolio" }
];

export const contactMethodOptions = [
  { value: "call", label: "Scheduled Video Call" },
  { value: "email", label: "Email Exchange" }
];

export const whyIpFundersContent = {
  manifesto: `Most founders understand that intellectual property matters. Fewer understand why the traditional model for creating IP is so poorly suited to startups.

For most technology companies, IP protection begins with patents. Patents are the most common starting point for IP protection, not its boundary. Patent prosecution requires significant upfront investment. Outcomes take years. Grant rates are uncertain. And the economic value depends on market factors you can't predict when you file. The traditional model demands commitment before clarity.

IpFunders exists to offer a different structure.

We fund IP protection upfront—starting with patents, where the timing risk is greatest. You pay modest periodic fees while applications are pending. As the IP takes shape and proves its value, your investment can grow accordingly. And when the IP becomes strategic, you can consolidate ownership and eliminate ongoing fees.

This isn't a loan. There's no debt, no interest, no repayment schedule. It's not equity financing. There's no dilution, no board seats. It's a structure that lets commitment align with clarity.

We manage U.S. patent prosecution internally, which means we can execute continuation and divisional strategy efficiently. As your needs evolve, there's no renegotiation required. The relationship is designed from the start to adapt.

IpFunders exists because we believe founders shouldn't have to choose between protecting their IP and preserving their capital. The right structure lets you do both.`,
  
  positioning: [
    {
      title: "We Are",
      points: [
        "An IP protection funding platform",
        "Focused on patents as the primary starting point",
        "A non-equity, non-debt capital partner",
        "Structured to align with founder success"
      ]
    },
    {
      title: "We Are Not",
      points: [
        "A patent law firm or filing service",
        "A litigation funder or patent assertion entity",
        "A venture fund seeking equity",
        "A lender expecting repayment"
      ]
    }
  ],
  
  riskTypes: [
    {
      title: "Prosecution Risk",
      description: "We fund upfront costs and absorb uncertainty of patent issuance"
    },
    {
      title: "Timing Risk",
      description: "Low fees while pending means you don't over-commit early"
    },
    {
      title: "Market Risk",
      description: "Adapt your position if circumstances change. Flexibility is built in."
    },
    {
      title: "Strategic Risk",
      description: "Investment follows value. Commitment aligns with clarity."
    }
  ]
};

export const startupsPageContent = {
  hero: {
    headline: "IP Protection Without Draining Your Runway",
    subheadline: "We fund IP protection—starting with patents—so you can conserve capital for product and growth. Investment scales only as your IP proves useful."
  },
  useCase: {
    situation: [
      "You have technology worth protecting",
      "IP costs hit before you know what the protection is worth",
      "Capital is better deployed on product, team, and customers",
      "You need flexibility to adapt as the business evolves"
    ],
    ipfundersRole: [
      "Fund patent prosecution from the start",
      "Shift upfront cost and uncertainty off your balance sheet",
      "Align investment with progress over time",
      "Preserve your flexibility and control throughout"
    ],
    outcome: [
      "Conserve capital for what matters most right now",
      "Avoid premature commitment to IP costs",
      "Scale IP investment only as the portfolio proves valuable",
      "Retain control as your strategy evolves"
    ]
  },
  features: [
    {
      title: "Capital Preservation",
      description: "Upfront costs shift to us. Your capital stays focused on building the business.",
      icon: "Wallet"
    },
    {
      title: "Avoid Premature Commitment",
      description: "Start protection without locking in large expenditures before value is clear.",
      icon: "ArrowLeftRight"
    },
    {
      title: "Investment Scales With Progress",
      description: "Commitment grows only as the IP matures and proves useful.",
      icon: "TrendingUp"
    },
    {
      title: "Control Preserved",
      description: "Flexibility to adapt your IP position as circumstances evolve.",
      icon: "Shield"
    }
  ]
};

export const corporatePageContent = {
  hero: {
    headline: "IP Protection With Portfolio-Level Optionality",
    subheadline: "Pursue more ideas without upfront over-allocation. Investment aligns with demonstrated value across your portfolio."
  },
  useCase: {
    situation: [
      "Internal teams generate ideas faster than budgets can absorb",
      "Budget cycles force premature go/no-go decisions",
      "Valuable concepts are abandoned due to timing, not merit",
      "IP strategy needs flexibility across multiple projects"
    ],
    ipfundersRole: [
      "Fund IP protection—starting with patents—without impacting internal budgets",
      "Shift upfront cost and uncertainty externally",
      "Align investment with progress across the portfolio",
      "Preserve strategic flexibility as priorities evolve"
    ],
    outcome: [
      "Evaluate ideas on merit, not budget availability",
      "Maintain capital efficiency across multiple initiatives",
      "Scale IP investment only where value emerges",
      "Retain strategic flexibility without over-commitment"
    ]
  },
  features: [
    {
      title: "Capital Efficiency",
      description: "Pursue IP protection without upfront allocation from internal budgets.",
      icon: "Filter"
    },
    {
      title: "Portfolio-Level Optionality",
      description: "Run multiple ideas in parallel without committing heavily to any single one.",
      icon: "LayoutGrid"
    },
    {
      title: "Strategic Flexibility",
      description: "Adapt IP priorities as business strategy evolves.",
      icon: "Shuffle"
    },
    {
      title: "Clean Risk Isolation",
      description: "Prosecution uncertainty stays off your balance sheet.",
      icon: "ShieldOff"
    }
  ]
};

export const inventorsPageContent = {
  hero: {
    headline: "IP Protection Without Personal Financial Risk",
    subheadline: "We fund patent protection so you can pursue ideas without immediate out-of-pocket exposure. You retain control as the IP matures."
  },
  features: [
    {
      title: "Reduced Personal Exposure",
      description: "We fund upfront prosecution costs. Your personal financial risk stays limited.",
      icon: "Wallet"
    },
    {
      title: "No Immediate Monetization Pressure",
      description: "Protection begins without forcing premature licensing or sale decisions.",
      icon: "Clock"
    },
    {
      title: "Investment Aligns With Progress",
      description: "Commitment grows only as the IP matures and its value becomes clearer.",
      icon: "TrendingUp"
    },
    {
      title: "Control Preserved",
      description: "Retain decision-making authority as circumstances evolve.",
      icon: "Shield"
    }
  ]
};

export const ndaPageContent = {
  headline: "Confidentiality as Foundation",
  subheadline: "NDA-first engagement protects IP value and enables honest evaluation.",
  whyRequired: {
    title: "Why Confidentiality Matters",
    points: [
      "IP loses value when disclosed publicly",
      "Honest evaluation requires detailed discussion",
      "Legal protection enables trust",
      "Both parties need security to engage fully"
    ]
  },
  principles: {
    title: "Our Approach",
    points: [
      "Mutual NDA with both parties protected",
      "Clear scope with coverage defined",
      "Standard terms with no hidden provisions",
      "Professional handling of all information"
    ]
  },
  nextSteps: {
    title: "After NDA",
    points: [
      "Deeper evaluation of your concept",
      "Discussion of technical and strategic merit",
      "Assessment of fit",
      "Path to engagement if aligned"
    ]
  }
};
